import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import algoliaInsights from 'search-insights';
import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { algoliasearch } from 'algoliasearch';
import { Configure, InstantSearch, useInstantSearch, useSortBy } from 'react-instantsearch';
import { Modal, NamedRedirect, Page } from '../../components';
import IconCollection from '../../components/IconCollection/IconCollection';
import { LISTING_STATE_PUBLISHED, listingFilters, PRIMARY_DESTINATIONS_FILTER, SECONDARY_DESTINATIONS_FILTER } from '../../constants';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { createRefinementList, useQuery } from '../../util/data';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import css from './AlgoliaSearchPage.module.css';
import Filters from './Filters/Filters';
import CustomHits from './Hits/Hits';
import CustomPagination from './Pagination/Pagination';
import AlgoliaSearchBar from './AlgoliaSearchBar/AlgoliaSearchBar';
import { currentUserIdSelector } from '../../ducks/user.duck';
import { useEffect } from 'react';

const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
const apiKey = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;
const listingIndex = process.env.REACT_APP_ALGOLIA_LISTINGS_INDEX;

const HITS_PER_PAGE = 9;

const searchClient = algoliasearch(appId, apiKey);

const prepareInitialFilters = ({initialRefinementList, initialRange}) => {
  const uiState = {};
  const filterState = {
    filters: `state:${LISTING_STATE_PUBLISHED}`,
  };

  // Initial Fefinements
  if (Object.keys(initialRefinementList || {}).length) {
    // remove primary destination filter when secondary is applied as some secondary destinations can be deselected to filter further
    if (initialRefinementList[SECONDARY_DESTINATIONS_FILTER]) {
      delete initialRefinementList?.[PRIMARY_DESTINATIONS_FILTER];
    }

    uiState.refinementList = initialRefinementList;
  }

  // Initial Range
  if (!!initialRange) {
    uiState.range = {
      ['price']: initialRange.replace('-', ':'),
    };
  }

  return {filterState, uiState};
};

const TotalResults = () => {
  const { results, indexUiState } = useInstantSearch();
  const { nbHits = 0 } = results;
  return (
    <div className={css.totalResults}>
      <FormattedMessage id="AlgoliaSearchPage.totalResults" values={{ nbHits }} />
    </div>
  );
};

const SortBy = ({ items }) => {
  const { currentRefinement, options, refine } = useSortBy({ items });
  return (
    <div className={css.sortByContainer}>
      <select onChange={event => refine(event.target.value)} value={currentRefinement}>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export const AlgoliaSearchPageComponent = props => {
  const {
    intl,
    location,
    scrollingDisabled,
    routeConfiguration,
    config,
    history,
    onManageDisableScrolling,
    params,
  } = props;

  algoliaInsights('init', {
    appId,
    apiKey,
  });

  const [showFilters, setShowFilters] = useState(false);
  const currentUserId = useSelector(currentUserIdSelector);
  const queryParams = Object.fromEntries(useQuery(location.search).entries());
  const filters = listingFilters;
  const indexName = listingIndex;

  // Redirect user to home page if index name is null
  if (!indexName) {
    return <NamedRedirect name="LandingPage" />;
  }

  const initialKeyword = useQuery(location.search).get('keywords');
  const refinementListAttributes = filters
    .map(filter => filter.attribute)
    .concat([SECONDARY_DESTINATIONS_FILTER]);
  const initialRefinementList = createRefinementList(queryParams, refinementListAttributes);
  const initialRange = queryParams['price'] || null;

  // Define initial UI state
  const { filterState, uiState } = prepareInitialFilters({initialRefinementList, initialRange});

  const initialUiState = {
    [indexName]: {
      ...uiState,
      configure: {
        ...filterState,
      },
    },
  };

  console.log('initialUiState', location.search, filterState)

  useEffect(() => {
    // Ensure userToken is set
    if (currentUserId) {
      algoliaInsights('setUserToken', currentUserId);
    } else {
      algoliaInsights('setUserToken', 'anonymous-user');
    }
  }, [currentUserId]);

  return (
    <Page scrollingDisabled={scrollingDisabled}>
      <TopbarContainer currentPage={'AlgoliaSearchPage'} />
      <InstantSearch
        key={`${indexName}-${location.search}`} // Forces re-initialization on indexName change
        indexName={indexName}
        searchClient={searchClient}
        initialUiState={{ ...initialUiState }}
        insights={true}
      >
        <Configure
          hitsPerPage={HITS_PER_PAGE}
          {...(initialKeyword?.trim() ? { query: initialKeyword } : {})}
          clickAnalytics={true}
          userToken={currentUserId}
        />
        <div className={css.layoutWrapperContainer}>
          <Modal
            id="algoliaSearchPage.filterModal"
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            className={css.filterModal}
          >
            <div className={css.filterColumnContent}>
              <Filters
                filters={filters}
                intl={intl}
                indexName={indexName}
                routeConfiguration={routeConfiguration}
                history={history}
                queryParams={queryParams}
              />
            </div>
          </Modal>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              <Filters
                filters={filters}
                intl={intl}
                indexName={indexName}
                routeConfiguration={routeConfiguration}
                history={history}
                queryParams={queryParams}
              />
            </div>
          </aside>

          <div className={css.layoutWrapperMain} role="main">
            <AlgoliaSearchBar
              keywords={initialKeyword}
              routeConfiguration={routeConfiguration}
              history={history}
            />
            <div className={css.searchPageHeader}>
              <TotalResults />
              <SortBy
                items={[
                  {
                    label: 'Sort By: Newest',
                    value: `${indexName}`,
                  },
                  {
                    label: 'Sort By: Oldest',
                    value: `${indexName}_timestamp_asc`,
                  },
                ]}
              />
            </div>
            <button onClick={() => setShowFilters(true)} className={css.filterButton}>
              <IconCollection icon="filter-icon" />
              <span>
                <FormattedMessage id="AlgoliaSearchPage.filters" />
              </span>
            </button>
            <div className={css.searchResultContainer}>
              <CustomHits
                routeConfiguration={routeConfiguration}
                history={history}
                indexName={indexName}
                intl={intl}
              />
            </div>
            <CustomPagination />
          </div>
        </div>
      </InstantSearch>
      <FooterContainer />
    </Page>
  );
};

AlgoliaSearchPageComponent.defaultProps = {
  searchParams: {},
};

AlgoliaSearchPageComponent.propTypes = {
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  onManageDisableScrolling: func.isRequired,
};

const EnhancedAlgoliaSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { currentUser, ...restOfProps } = props;

  return (
    <AlgoliaSearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AlgoliaSearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  EnhancedAlgoliaSearchPage
);

export default AlgoliaSearchPage;
